import { useAuth, useUser } from '@clerk/react';
import { useEffect, useRef } from 'react';

import { FUNNEL_EVENTS, trackFunnel } from './funnel';
import { ensurePostHog } from './posthog-client';

const SIGNUP_TRACKED_PREFIX = 'roam:analytics:signup:';
const FRESH_ACCOUNT_MS = 30 * 60 * 1000;

function signupStorageKey(clerkUserId: string) {
  return `${SIGNUP_TRACKED_PREFIX}${clerkUserId}`;
}

/**
 * Clerk-backed identify + one-time signup funnel event. Lives under
 * `ClerkProvider` so `useUser` is available; never sends email or names to
 * PostHog — only the opaque Clerk user id as `distinct_id`.
 */
export function PostHogIdentityBridge() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const lastIdentifiedId = useRef<string | null>(null);

  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      if (lastIdentifiedId.current !== null) {
        void ensurePostHog()
          .then((posthog) => posthog?.reset())
          .catch(() => undefined);
        lastIdentifiedId.current = null;
      }
      return;
    }

    if (lastIdentifiedId.current === user.id) return;
    lastIdentifiedId.current = user.id;

    void ensurePostHog()
      .then((posthog) => {
        if (!posthog) return;
        posthog.identify(user.id);

        const createdAt = user.createdAt?.getTime();
        const storageKey = signupStorageKey(user.id);
        const alreadyTracked =
          typeof window !== 'undefined' && window.localStorage.getItem(storageKey) === '1';
        const isFreshAccount =
          typeof createdAt === 'number' && Date.now() - createdAt <= FRESH_ACCOUNT_MS;

        if (isFreshAccount && !alreadyTracked) {
          trackFunnel(FUNNEL_EVENTS.USER_SIGNED_UP, {});
          window.localStorage.setItem(storageKey, '1');
        }
      })
      .catch(() => undefined);
  }, [isSignedIn, user?.createdAt, user?.id]);

  return null;
}
